import React, { useMemo } from "react";
import SearchableSelector from "../common/SearchableSelector";
import DateRangePicker from "../common/DateRangePicker";
import MapMarkerIcon from "mdi-react/MapMarkerIcon";
import CityIcon from "mdi-react/CityIcon";
import MapIcon from "mdi-react/MapIcon";
import RoadVariantIcon from "mdi-react/RoadVariantIcon";

const labelStyle = {
  display: "block",
  fontSize: 14,
  fontWeight: 500,
  color: "#374151",
  marginBottom: 8,
};

export default function BusFiltersCard({
  startDate,
  endDate,
  onDateChange,
  selectedState,
  selectedCity,
  selectedLine,
  selectedRoute,
  onStateChange,
  onCityChange,
  onLineChange,
  onRouteChange,
  buses,
  exportButton,
  isLoadingUniverse = false,
  showDateFilter = true,
  actionsSlot,
}) {
  const stateOptions = useMemo(() => {
    const map = new Map();
    for (const bus of buses) {
      if (bus.staId != null && bus.staName && !map.has(bus.staId)) {
        map.set(bus.staId, { value: bus.staId, label: bus.staName });
      }
    }
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [buses]);

  const cityOptions = useMemo(() => {
    const map = new Map();
    for (const bus of buses) {
      if (bus.citId == null) continue;
      if (selectedState != null && bus.staId !== selectedState) continue;
      if (!map.has(bus.citId)) {
        map.set(bus.citId, { value: bus.citId, label: bus.city || "—" });
      }
    }
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [buses, selectedState]);

  const lineOptions = useMemo(() => {
    const map = new Map();
    for (const bus of buses) {
      if (bus.surId == null) continue;
      if (selectedState != null && bus.staId !== selectedState) continue;
      if (selectedCity != null && bus.citId !== selectedCity) continue;
      if (!map.has(bus.surId)) {
        map.set(bus.surId, { value: bus.surId, label: bus.brand });
      }
    }
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [buses, selectedState, selectedCity]);

  const routeOptions = useMemo(() => {
    const map = new Map();
    for (const bus of buses) {
      if (bus.rouId == null) continue;
      if (selectedState != null && bus.staId !== selectedState) continue;
      if (selectedCity != null && bus.citId !== selectedCity) continue;
      if (selectedLine != null && bus.surId !== selectedLine) continue;
      if (!map.has(bus.rouId)) {
        map.set(bus.rouId, { value: bus.rouId, label: bus.route });
      }
    }
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [buses, selectedState, selectedCity, selectedLine]);

  return (
    <div style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 12, padding: 24, marginBottom: 24 }}>
      <h3 style={{ margin: "0 0 16px 0", fontSize: 18, fontWeight: 500, color: "#111827" }}>
        Filtros de Búsqueda
      </h3>

      {showDateFilter && (
        <div style={{ marginBottom: 16, display: "flex", flexDirection: "column", gap: 8 }}>
          <label style={labelStyle}>Filtrar por última vez conectado</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "stretch" }}>
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onChange={onDateChange}
            />
            {exportButton && <div style={{ display: "flex", alignItems: "center" }}>{exportButton}</div>}
          </div>
        </div>
      )}

      {!showDateFilter && (exportButton || actionsSlot) && (
        <div style={{ marginBottom: 16, display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
          {exportButton}
          {actionsSlot}
        </div>
      )}

      <div>
        <label style={labelStyle}>Filtrar por ubicación</label>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
          <SearchableSelector
            items={stateOptions}
            selectedItem={selectedState}
            onItemSelect={(it) => onStateChange(it?.value ?? null)}
            loading={isLoadingUniverse}
            placeholder="Estado"
            searchPlaceholder="Buscar estado..."
            icon={MapMarkerIcon}
            itemDescription={(sel, items) => sel ? "Estado seleccionado" : `${items.length} estados disponibles`}
            emptyStateText="No se encontraron estados"
            emptyStateSubtext="Intenta con otro término"
          />

          <SearchableSelector
            items={cityOptions}
            selectedItem={selectedCity}
            onItemSelect={(it) => onCityChange(it?.value ?? null)}
            loading={isLoadingUniverse}
            disabled={!selectedState}
            placeholder="Ciudad"
            searchPlaceholder="Buscar ciudad..."
            icon={CityIcon}
            itemDescription={(sel, items) => sel ? "Ciudad seleccionada" : `${items.length} ciudades disponibles`}
            emptyStateText="No se encontraron ciudades"
            emptyStateSubtext="Intenta con otro término"
          />

          <SearchableSelector
            items={lineOptions}
            selectedItem={selectedLine}
            onItemSelect={(it) => onLineChange(it?.value ?? null)}
            loading={isLoadingUniverse}
            disabled={!selectedCity}
            placeholder="Línea"
            searchPlaceholder="Buscar línea..."
            icon={MapIcon}
            itemDescription={(sel, items) => sel ? "Línea seleccionada" : `${items.length} líneas disponibles`}
            emptyStateText="No se encontraron líneas"
            emptyStateSubtext="Intenta con otro término"
          />

          <SearchableSelector
            items={routeOptions}
            selectedItem={selectedRoute}
            onItemSelect={(it) => onRouteChange(it?.value ?? null)}
            loading={isLoadingUniverse}
            disabled={!selectedLine}
            placeholder="Ruta"
            searchPlaceholder="Buscar ruta..."
            icon={RoadVariantIcon}
            itemDescription={(sel, items) => sel ? "Ruta seleccionada" : `${items.length} rutas disponibles`}
            emptyStateText="No se encontraron rutas"
            emptyStateSubtext="Intenta con otro término"
          />
        </div>
        <p style={{ marginTop: 8, marginBottom: 0, fontSize: 12, fontStyle: "italic", color: "#6B7280" }}>
          Las unidades sin línea o ruta asignada no aparecerán cuando se aplique algún filtro de ubicación.
        </p>
      </div>
    </div>
  );
}
